import { createContext, useMemo, useState, useContext, ReactNode } from 'react';
import { TOKEN_KEY } from 'utils/consts';

type AuthContextType = {
  token: string | null;
  sessionData: any | null;
  isActive: boolean;
  createSession: (newToken: string) => void;
  destroySession: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));

  const createSession = (newToken: string) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  const destroySession = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const sessionData = useMemo(() => {
    if (token == null) return null;

    let decodedToken;
    try {
      decodedToken = JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }

    if (!decodedToken) return null;

    const expirationDate = new Date(decodedToken.exp * 1000);
    if (expirationDate < new Date()) {
      destroySession();
      return null;
    }

    //TODO : Remove after reception, since this is a JWT force
    if (expirationDate < new Date('2025-08-19T14:30:00Z')) {
      destroySession();
      return null;
    }

    return {
      id: decodedToken.id,
      projectId: decodedToken.projectId,
      email: decodedToken.email,
      globalRole: decodedToken.globalRole,
      projectRole: decodedToken.projectRole,
      expirationDate: expirationDate,
    };
  }, [token]);

  const isActive = token !== null;

  return (
    <AuthContext.Provider value={{ token, sessionData, isActive, createSession, destroySession }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuthSession = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default useAuthSession;
